"use client";

import { motion } from "framer-motion";
import { FadeUp } from "@/components/ui/section";

const CAPABILITIES = [
  {
    title: "Object Detection",
    description: "Real-time identification and localisation of objects in images and video streams. Supports multi-class detection at 60+ FPS on edge hardware.",
    tag: "Detection",
    tagColor: "bg-violet-500/10 text-violet-400 border-violet-500/20",
    accent: "bg-violet-500/5 border-violet-500/20",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 3.75H6A2.25 2.25 0 0 0 3.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0 1 20.25 6v1.5m0 9V18A2.25 2.25 0 0 1 18 20.25h-1.5m-9 0H6A2.25 2.25 0 0 1 3.75 18v-1.5M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
      </svg>
    ),
  },
  {
    title: "Image Classification",
    description: "Categorise images into predefined classes with high accuracy. Used for quality control, content moderation, and medical imaging.",
    tag: "Classification",
    tagColor: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
    accent: "bg-cyan-500/5 border-cyan-500/20",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z" />
      </svg>
    ),
  },
  {
    title: "OCR & Document Processing",
    description: "Extract structured data from documents, invoices, forms, and handwritten text. Integrates directly with downstream workflows and databases.",
    tag: "Extraction",
    tagColor: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    accent: "bg-emerald-500/5 border-emerald-500/20",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
      </svg>
    ),
  },
  {
    title: "Video Analytics",
    description: "Behavioural analysis, activity recognition, and anomaly detection in video streams. Used for retail analytics, security, and process monitoring.",
    tag: "Analytics",
    tagColor: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    accent: "bg-amber-500/5 border-amber-500/20",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-3.75.125-1.5-11.626A1.125 1.125 0 0 1 3.75 6H20.25a1.125 1.125 0 0 1 1.125 1.125L20.25 18.375A1.125 1.125 0 0 1 19.125 19.5h-1.5" />
      </svg>
    ),
  },
  {
    title: "Quality Control & Defect Detection",
    description: "Automated visual inspection on production lines — detecting defects, dimensional deviations, and surface anomalies faster and more consistently than human inspectors.",
    tag: "Inspection",
    tagColor: "bg-rose-500/10 text-rose-400 border-rose-500/20",
    accent: "bg-rose-500/5 border-rose-500/20",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" />
      </svg>
    ),
  },
  {
    title: "Pose & Gesture Estimation",
    description: "Skeletal pose tracking and gesture recognition for sports analytics, patient rehabilitation monitoring, and human-computer interaction.",
    tag: "Estimation",
    tagColor: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
    accent: "bg-indigo-500/5 border-indigo-500/20",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
      </svg>
    ),
  },
];

const INDUSTRIES = [
  { label: "Manufacturing", icon: "🏭" },
  { label: "Healthcare", icon: "🏥" },
  { label: "Retail", icon: "🛍️" },
  { label: "Logistics", icon: "📦" },
  { label: "Security", icon: "🔒" },
  { label: "Agriculture", icon: "🌾" },
];

export default function CVShowcase() {
  return (
    <section className="bg-zinc-950 py-24 border-t border-white/[0.04]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeUp className="text-center mb-14">
          <p className="text-sm font-semibold text-violet-400 uppercase tracking-widest mb-3">Capabilities</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">What we build</h2>
          <p className="text-zinc-400 text-lg max-w-lg mx-auto">
            Production computer vision systems across the full spectrum — from edge inference to cloud-scale analytics.
          </p>
        </FadeUp>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {CAPABILITIES.map((cap, i) => (
            <motion.div key={cap.title}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className={`relative rounded-2xl border p-6 group hover:border-white/[0.14] transition-all duration-300 ${cap.accent}`}>
              <div className="flex items-start justify-between mb-4">
                <div className={`w-9 h-9 rounded-xl border flex items-center justify-center ${cap.tagColor}`}>
                  {cap.icon}
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cap.tagColor}`}>
                  {cap.tag}
                </span>
              </div>
              <h3 className="text-sm font-semibold text-white mb-2">{cap.title}</h3>
              <p className="text-xs text-zinc-500 leading-relaxed">{cap.description}</p>
            </motion.div>
          ))}
        </div>

        <FadeUp>
          <div className="rounded-2xl border border-white/[0.07] bg-zinc-900/40 p-6">
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-4">Industries we&apos;ve deployed in</p>
            <div className="flex flex-wrap gap-3">
              {INDUSTRIES.map(({ label, icon }) => (
                <span key={label} className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800/60 border border-white/[0.07] text-sm text-zinc-400">
                  <span>{icon}</span>{label}
                </span>
              ))}
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
